import React, { useEffect, useRef, useState } from 'react';
import { extent, max } from '@visx/vendor/d3-array';
import { scaleLinear, scaleUtc } from '@visx/scale';
import { Group } from '@visx/group';
import { LinePath } from '@visx/shape';
import { curveNatural } from '@visx/curve';
import { AxisBottom } from '@visx/axis';

import { HomePageSection } from 'src/components/HomePage/HomePageSection';
import { RegularCardBox } from 'src/components/Cards/RegularCardBox';
import BaseTypography from 'src/components/Text/BaseTypography';
import Button from 'src/components/Button';
import { InfoOutlined } from '@material-ui/icons';
import history from 'src/history';
import { AnalyticsDailyStats } from 'src/types/analytics';
import { useAppSelector } from 'src/hooks/useStore';

type ChartData = Pick<AnalyticsDailyStats, 'total' | 'date'>;

const MAX_CLIENTS = 100;

function getRandomInt(minBoundary: number, maxBoundary: number): number {
  const minInt = Math.ceil(minBoundary);
  const maxInt = Math.floor(maxBoundary);
  return Math.floor(Math.random() * (maxInt - minInt + 1)) + minInt;
}

const today = new Date();
const data: ChartData[] = new Array(30)
  .fill(null)
  .reduce<ChartData[]>((acc, _, i) => {
    const prevTotal = acc[acc.length - 1]?.total ?? 0;
    // Make sure we add some people on day 2, so we're not always multiplying against a 0 total.
    const added =
      i === 1 ? 3 : getRandomInt(2, Math.min(12, MAX_CLIENTS - prevTotal));
    const removed = getRandomInt(0, Math.min(6, prevTotal - 1));
    const date = new Date(today);
    date.setDate(today.getDate() - (30 - i));

    acc.push({
      date: date,
      total: prevTotal + added - removed,
    });

    return acc;
  }, []);

// Data accessors
const getX = (d: ChartData) => d.date;
const getY = (d: ChartData) => d.total;

const isDateTuple = (tuple: [unknown, unknown]): tuple is [Date, Date] => {
  return tuple.every((d) => d instanceof Date);
};

const ensureDateTuple = (tuple: [unknown, unknown]): [Date, Date] => {
  if (!isDateTuple(tuple)) {
    throw new Error('Invalid date tuple');
  }
  return tuple;
};

// Axis scales
const getScale = () => {
  const y = max(data, getY);
  if (typeof y !== 'number') {
    throw new Error('Value missing for y axis');
  }
  return {
    x: scaleUtc<number>({
      domain: ensureDateTuple(extent(data, getX)),
    }),
    y: scaleLinear<number>({
      domain: [0, y],
    }),
  };
};

export function EmptyState() {
  const clientUsers = useAppSelector((state) => state.clients.clients);
  const sizeReference = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(getScale());

  useEffect(() => {
    const handleResize = () => {
      if (!sizeReference.current) return;
      const { width, height } = sizeReference.current.getBoundingClientRect();
      const updatedScale = getScale();
      updatedScale.x.range([0, width]);
      updatedScale.y.range([height - 24, 0]);
      setScale(updatedScale);
    };
    addEventListener('resize', handleResize);
    handleResize();
    return () => removeEventListener('resize', handleResize);
  }, [sizeReference]);

  return (
    <HomePageSection title="Analytics" subTitle="">
      <RegularCardBox className="p-6">
        <header className="pb-6 flex flex-row items-center">
          {/* Unfortunately !important is needed to override MUI styles. */}
          <InfoOutlined className="!w-4 !h-4 pr-1.5" />
          <BaseTypography fontType="14Regular" className="leading-none flex-1">
            Metrics will show after you create 10 clients ({clientUsers.length}
            /10 created so far)
          </BaseTypography>
          <Button
            onClick={() => history.push('/clients/users')}
            className="px-2 py-1 text-sm border border-solid border-gray-300 rounded"
            htmlId={'analytics-go-to-crm'}
          >
            Go to CRM
          </Button>
        </header>
        <div className="text-gray-500 h-35 w-full" ref={sizeReference}>
          <div className="flex pb-5">
            <div className="w-6 pr-4 box-content">
              <div className="relative w-full h-full mt-[-0.5rem]">
                <BaseTypography
                  className="leading-none absolute top-0 right-0"
                  fontType="13Regular"
                >
                  100
                </BaseTypography>
                <BaseTypography
                  className="leading-none absolute top-[50%] right-0"
                  fontType="13Regular"
                >
                  50
                </BaseTypography>
                <BaseTypography
                  className="leading-none absolute top-[100%] right-0"
                  fontType="13Regular"
                >
                  0
                </BaseTypography>
              </div>
            </div>
            <svg className="w-full h-full border-y border-y-gray-300 text-gray-300 border-solid border-x-0">
              <Group>
                <line
                  x1="0%"
                  x2="100%"
                  y1="50%"
                  y2="50%"
                  stroke="currentColor"
                />
                <LinePath<ChartData>
                  curve={curveNatural}
                  data={data}
                  shapeRendering="geometricPrecision"
                  stroke="currentColor"
                  strokeOpacity={1}
                  strokeWidth={1}
                  x={(d) => {
                    return scale.x(getX(d)) ?? 0;
                  }}
                  y={(d) => {
                    return scale.y(getY(d)) ?? 0;
                  }}
                />
              </Group>
            </svg>
          </div>
          <svg className="w-full h-5 text-gray-300">
            <AxisBottom
              axisClassName="fill-current"
              axisLineClassName="stroke-current"
              hideTicks
              hideAxisLine
              // Go with a low number for the empty state, we can expand
              // it when real data is present.
              numTicks={4}
              scale={scale.x}
              tickComponent={({ x, formattedValue }) => (
                <text
                  className="fill-gray-500"
                  x={x}
                  y={6} // No idea why, it just looks right this way.
                  dy=".33em"
                  fontSize={13}
                  textAnchor="middle"
                >
                  {formattedValue}
                </text>
              )}
            />
          </svg>
        </div>
      </RegularCardBox>
    </HomePageSection>
  );
}
